import { Component, EventEmitter, Input, Output } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, Validators } from '@angular/forms';
import { AuthenticationService } from 'src/app/core/authentication/authentication.service';
import { AppConfigService, AppConfiguration } from 'src/app/core/service/app-config.service';
import { MemberService } from 'src/app/core/service/member.service';
import { SettingService } from 'src/app/core/service/setting.service';
import { UserService } from 'src/app/core/service/user.service';
import { MessageHandlerService } from 'src/app/core/utility/message-handler.service';

@Component({
  standalone: false,
  selector: 'app-member-send-selection',
  templateUrl: './member-send-selection.component.html',
  styleUrls: ['./member-send-selection.component.scss']
})
export class MemberSendSelectionComponent
{
  @Input() public type: string | 'CONSENT' | 'INVITE';
  @Input() public memberSelected: any;

  @Output() public closing: EventEmitter<boolean> = new EventEmitter();

  public sending = false;
  public selectionForm = undefined;
  public loggedInUser = null;
  public loggedInUserRoleText = '';
  public isOpen = false;
  public patientLabel = {};
  public phoneNumberMask: string;
  public languages: Array<{ text: string; value: string }>;

  private appConfig: AppConfiguration;

  constructor(
    private memberService: MemberService,
    private userService: UserService,
    private settingService: SettingService,
    private authenticationService: AuthenticationService,
    private fb: UntypedFormBuilder,
    private messageHandlerService: MessageHandlerService,
    private appConfigService: AppConfigService)
  {
    this.appConfig = this.appConfigService.getConfig();
    this.languages = this.userService.preferredLanguages;
  }

  public get methodRequest(): UntypedFormControl
  {
    return this.selectionForm.get('methodRequest');
  }

  public get otherPhone(): UntypedFormControl
  {
    return this.selectionForm.get('otherPhone');
  }

  public get otherEmail(): UntypedFormControl
  {
    return this.selectionForm.get('otherEmail');
  }

  public get preferredLanguage(): UntypedFormControl
  {
    return this.selectionForm.get('preferredLanguage');
  }

  @Input() public set open(open: boolean)
  {
    if (open)
    {
      this.settingService.getCachedSessionSettings().subscribe({
        next: (sessionSetting) =>
        {
          this.patientLabel = { patientLabel: sessionSetting.patientLabel };
          this.phoneNumberMask = sessionSetting.phoneMask;
          this.userService.getUserById(this.authenticationService.currentUser.id, false).subscribe({
            next: (user) =>
            {
              this.loggedInUser = user;
              this.loggedInUserRoleText = this.userService.roles.find(x => x.value === this.loggedInUser.role)?.text;

              this.selectionForm = this.fb.group({
                preferredLanguage: [{ value: 'English', disabled: false }, { validators: [Validators.required] }],
                methodRequest: [{ value: null, disabled: false }, { validators: [Validators.required] }],
                otherPhone: [{ value: null, disabled: true }],
                otherEmail: [{ value: null, disabled: true }]
              });

              this.selectionForm.get('methodRequest').valueChanges.subscribe({
                next: (method: string) =>
                {
                  this.otherPhone.disable();
                  this.otherPhone.setValidators([]);
                  this.otherEmail.disable();
                  this.otherEmail.setValidators([]);

                  switch (method)
                  {
                    case 'otherSMS':
                      this.otherPhone.enable();
                      this.otherPhone.setValidators([Validators.required]);
                      break;
                    case 'otherEmail':
                      this.otherEmail.enable();
                      this.otherEmail.setValidators([Validators.required, Validators.email]);
                      break;
                    case 'patientEmail':
                    case 'patientSMS':
                      if (this.memberSelected.preferredLanguage)
                      {
                        this.setPreferredLanguage(this.memberSelected.preferredLanguage);
                      }
                      break;
                    case 'loggedInUserEmail':
                    case 'loggedInUserSMS':
                      if (this.loggedInUser.preferredLanguage)
                      {
                        this.setPreferredLanguage(this.loggedInUser.preferredLanguage);
                      }
                      break;
                  }
                  this.otherPhone.updateValueAndValidity();
                  this.otherEmail.updateValueAndValidity();
                }
              });
              this.isOpen = open;
            }
          });
        }
      });
    }
  }

  public send(): void
  {
    this.sending = true;
    const request = {
      memberId: this.memberSelected.id,
      type: this.methodRequest.value,
      email: null,
      phone: null,
      preferredLanguage: this.preferredLanguage.value,
      baseUrl: this.appConfig.community.baseUrl
    };

    // Set the email or phone depending on the user selection
    switch (this.methodRequest.value)
    {
      case 'patientSMS':
        request.phone = this.memberSelected.cellPhoneNumber;
        break;
      case 'patientEmail':
        request.email = this.memberSelected.email;
        break;
      case 'loggedInUserSMS':
        request.phone = this.loggedInUser?.phoneNumber;
        break;
      case 'loggedInUserEmail':
        request.email = this.loggedInUser?.email;
        break;
      case 'otherSMS':
        request.phone = this.otherPhone.value;
        break;
      case 'otherEmail':
        request.email = this.otherEmail.value;
        break;
    }

    let methodName = '';
    let messageKey = '';

    switch (this.type)
    {
      case 'CONSENT':
        methodName = 'createConsent';
        messageKey = 'CONSENT-REQUESTED-SUCCESSFULLY';
        break;
      case 'INVITE':
        methodName = 'createInvite';
        messageKey = 'COMMUNITY-INVITE-SENT-SUCCESSFULLY';
        break;
    }

    this.memberService[methodName](request).subscribe({
      next: () =>
      {
        this.messageHandlerService.displaySuccess(messageKey);
        this.closing.emit(true);
      },
      error: (errorResponse: any) =>
      {
        this.messageHandlerService.displayError(errorResponse);
      },
      complete: () =>
      {
        this.memberSelected = null;
        this.sending = false;
        this.isOpen = false;
      }
    });
  }

  public close(): void
  {
    this.isOpen = false;
    this.closing.emit(false);
  }

  /**
   * Set the default preferred language.
   *
   * @param preferredLanguage Language value to set.
   */
  private setPreferredLanguage(preferredLanguage: string): void
  {
    const defaultLanguage = this.languages.find(x => x.value === preferredLanguage);
    if (defaultLanguage)
    {
      this.preferredLanguage.setValue(defaultLanguage.value);
      this.preferredLanguage.updateValueAndValidity();
    }
  }
}
